class TopbarFilter extends HTMLElement {
  constructor() {
    super();
    this.filterButtons = this.querySelectorAll('.topbar-filter__button');
    
    // Get selection mode from data attribute
    this.selectionMode = this.getAttribute('data-selection-mode') || 'single';
    
    // Add safety flags
    this.isInitialized = false;
    
    this.initializeFilters();
    this.bindEvents();
    
    this.isInitialized = true;
  }

  initializeFilters() {
    // Get current URL parameters
    this.currentParams = new URLSearchParams(window.location.search);
    
    // First, remove active state from all buttons
    this.filterButtons.forEach(btn => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-pressed', 'false');
    });
    
    // Check which filters are active based on URL
    this.filterButtons.forEach(btn => {
      const filterKey = btn.getAttribute('data-filter-key');
      const filterValue = btn.getAttribute('data-filter-value');
      
      // Skip ALL button (no filter key)
      if (!filterKey || !filterValue) {
        return;
      }
      
      const currentValue = this.currentParams.get(filterKey);
      
      if (currentValue) {
        if (this.selectionMode === 'multi') {
          // Multi-selection: check if value is in comma-separated list
          const values = currentValue.split(',');
          if (values.includes(filterValue)) {
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
          }
        } else {
          // Single-selection: exact match
          if (currentValue === filterValue) {
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
          }
        }
      }
    });
    
    // If no filters are active, activate ALL
    const hasActiveFilter = Array.from(this.filterButtons).some(btn => 
      btn.classList.contains('is-active') && btn.getAttribute('data-filter-key')
    );
    
    if (!hasActiveFilter) {
      const allButton = Array.from(this.filterButtons).find(btn => !btn.getAttribute('data-filter-key'));
      if (allButton) {
        allButton.classList.add('is-active');
        allButton.setAttribute('aria-pressed', 'true');
      }
    }
  }

  bindEvents() {
    // Bind filter button clicks
    this.filterButtons.forEach((button) => {
      button.addEventListener('click', this.onFilterClick.bind(this));
    });

    // Listen for custom filter events
    document.addEventListener('topbar-filter:apply', this.handleCustomFilterEvent.bind(this));
    
    // Listen for URL changes from facet system
    window.addEventListener('popstate', this.onHistoryChange.bind(this));
  }



  onHistoryChange(event) {
    // Update active filter button when user uses back/forward buttons
    const params = new URLSearchParams(window.location.search);
    
    // First, remove active state from all buttons
    this.filterButtons.forEach(btn => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-pressed', 'false');
    });
    
    // Check which filters are active based on URL
    this.filterButtons.forEach(btn => {
      const filterKey = btn.getAttribute('data-filter-key');
      const filterValue = btn.getAttribute('data-filter-value');
      
      // Skip ALL button
      if (!filterKey || !filterValue) {
        return;
      }
      
      const currentValue = params.get(filterKey);
      
      if (currentValue) {
        if (this.selectionMode === 'multi') {
          const values = currentValue.split(',');
          if (values.includes(filterValue)) {
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
          }
        } else {
          if (currentValue === filterValue) {
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
          }
        }
      }
    });
    
    // If no filters are active, activate ALL
    const hasActiveFilter = Array.from(this.filterButtons).some(btn => 
      btn.classList.contains('is-active') && btn.getAttribute('data-filter-key')
    );
    
    if (!hasActiveFilter) {
      const allButton = Array.from(this.filterButtons).find(btn => !btn.getAttribute('data-filter-key'));
      if (allButton) {
        allButton.classList.add('is-active');
        allButton.setAttribute('aria-pressed', 'true');
      }
    }
  }

  onFilterClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const filterKey = button.getAttribute('data-filter-key');
    const filterValue = button.getAttribute('data-filter-value');
    const isCurrentlyActive = button.classList.contains('is-active');
    const isAllButton = !filterKey; // ALL button has no filter-key

    // Handle clicking ALL button
    if (isAllButton) {
      // Clear all filters
      this.filterButtons.forEach(btn => {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      });
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
      this.applyFilter('all');
      return;
    }

    // Handle multi-selection mode
    if (this.selectionMode === 'multi') {
      // Clicking a filter when ALL is active: deactivate ALL, activate clicked
      const allButton = Array.from(this.filterButtons).find(btn => !btn.getAttribute('data-filter-key'));
      if (allButton && allButton.classList.contains('is-active')) {
        allButton.classList.remove('is-active');
        allButton.setAttribute('aria-pressed', 'false');
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        // Toggle the clicked filter
        if (isCurrentlyActive) {
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
          
          // If no filters are active, activate ALL
          const hasActiveFilters = Array.from(this.filterButtons).some(btn => 
            btn.getAttribute('data-filter-key') && btn.classList.contains('is-active')
          );
          if (!hasActiveFilters && allButton) {
            allButton.classList.add('is-active');
            allButton.setAttribute('aria-pressed', 'true');
          }
        } else {
          button.classList.add('is-active');
          button.setAttribute('aria-pressed', 'true');
        }
      }

      // Collect all active filters grouped by key
      const activeFilters = {};
      Array.from(this.filterButtons)
        .filter(btn => btn.classList.contains('is-active') && btn.getAttribute('data-filter-key'))
        .forEach(btn => {
          const key = btn.getAttribute('data-filter-key');
          const value = btn.getAttribute('data-filter-value');
          if (!activeFilters[key]) {
            activeFilters[key] = [];
          }
          activeFilters[key].push(value);
        });

      this.applyFilter(Object.keys(activeFilters).length > 0 ? activeFilters : 'all');
      return;
    }

    // Handle single-selection mode
    // If clicking the same active filter, toggle it off
    if (isCurrentlyActive) {
      // Add toggle animation
      button.classList.add('is-toggling');
      setTimeout(() => button.classList.remove('is-toggling'), 300);
      
      // Remove active state from all buttons
      this.filterButtons.forEach(btn => {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      });
      
      // Activate the ALL button
      const allButton = Array.from(this.filterButtons).find(btn => !btn.getAttribute('data-filter-key'));
      if (allButton) {
        allButton.classList.add('is-active');
        allButton.setAttribute('aria-pressed', 'true');
      }
      
      // Clear the filter
      this.applyFilter('all');
      return;
    }

    // Remove active state from all buttons
    this.filterButtons.forEach(btn => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-pressed', 'false');
    });
    
    // Add active state to clicked button
    button.classList.add('is-active');
    button.setAttribute('aria-pressed', 'true');

    // Apply the filter
    this.applyFilter({ [filterKey]: [filterValue] });
  }



  applyFilter(filters) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    
    if (filters === 'all') {
      // Remove all filter parameters (keep non-filter params like page, sort, etc.)
      const filterKeys = new Set();
      Array.from(this.filterButtons)
        .filter(btn => btn.getAttribute('data-filter-key'))
        .forEach(btn => filterKeys.add(btn.getAttribute('data-filter-key')));
      
      filterKeys.forEach(key => params.delete(key));
    } else if (typeof filters === 'object') {
      // Clear existing filters that are managed by topbar filter
      const allFilterKeys = new Set();
      Array.from(this.filterButtons)
        .filter(btn => btn.getAttribute('data-filter-key'))
        .forEach(btn => allFilterKeys.add(btn.getAttribute('data-filter-key')));
      
      allFilterKeys.forEach(key => params.delete(key));
      
      // Apply new filters
      Object.keys(filters).forEach(key => {
        const values = filters[key];
        if (values && values.length > 0) {
          // Join multiple values with comma for multi-selection
          params.set(key, values.join(','));
        }
      });
    }
    
    // Log for debugging
    console.log('applyFilter - filters:', filters, 'params:', params.toString());

    // Trigger filter application
    this.updatePage(params);
  }





  updatePage(params) {
    const paramsString = params.toString();
    const newUrl = `${window.location.pathname}${paramsString ? '?' + paramsString : ''}`;

    // Show theme's loading overlay
    const loadingOverlay = document.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('visibility-hidden');
    }

    // Check if main facet system exists
    const facetForm = document.querySelector('facet-filters-form');
    
    // Try to use AJAX to update the page without reload
    if (facetForm && typeof FacetFiltersForm !== 'undefined') {
      // Use the existing facet system if available
      try {
        console.log('Using facet system - params:', paramsString);
        
        FacetFiltersForm.renderPage(paramsString, null);
        
        // Hide loading overlay after render
        setTimeout(() => {
          if (loadingOverlay) {
            loadingOverlay.classList.remove('visibility-hidden');
          }
        }, 500);
      } catch (error) {
        console.warn('Facet system error, using custom AJAX', error);
        this.updatePageAjax(newUrl, paramsString);
      }
    } else {
      // Use our own AJAX implementation
      console.log('Using custom AJAX - params:', paramsString);
      this.updatePageAjax(newUrl, paramsString);
    }

    // Dispatch custom event for other components to listen
    this.dispatchFilterEvent(paramsString);
  }

  updatePageAjax(url, paramsString) {
    // Show theme's loading overlay
    const loadingOverlay = document.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('visibility-hidden');
    }
    
    // Fetch the new page content via AJAX
    fetch(url, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.text();
    })
    .then(html => {
      // Parse the response HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update the product grid
      const newProductGrid = doc.querySelector('#ProductGridContainer');
      const currentProductGrid = document.querySelector('#ProductGridContainer');
      
      if (newProductGrid && currentProductGrid) {
        currentProductGrid.innerHTML = newProductGrid.innerHTML;
        console.log('Product grid updated via AJAX');
      }
      
      // Update URL without reload
      history.pushState({ searchParams: paramsString }, '', url);
      
      // Hide loading overlay
      if (loadingOverlay) {
        loadingOverlay.classList.remove('visibility-hidden');
      }
      
      // Scroll to top of product grid
      if (currentProductGrid) {
        currentProductGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    })
    .catch(error => {
      console.error('AJAX filter error:', error);
      console.log('Falling back to page reload');
      // Hide loading overlay before fallback
      if (loadingOverlay) {
        loadingOverlay.classList.remove('visibility-hidden');
      }
      // Fallback to page reload on error
      window.location.href = url;
    });
  }

  dispatchFilterEvent(searchParams) {
    const event = new CustomEvent('topbar-filter:changed', {
      detail: {
        searchParams: searchParams,
        filters: this.getActiveFilters()
      },
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);
  }

  getActiveFilters() {
    const activeButton = this.querySelector('.topbar-filter__button.is-active');
    return {
      tag: activeButton ? activeButton.getAttribute('data-tag') : 'all'
    };
  }

  // Handle custom filter events from external sources
  handleCustomFilterEvent(event) {
    const { filterKey, filterValue, tag } = event.detail;

    // Support legacy tag-based API
    if (tag !== undefined) {
      // Find button by filter value (backwards compatibility)
      const targetButton = Array.from(this.filterButtons).find(
        btn => btn.getAttribute('data-filter-value') === tag
      );

      if (targetButton) {
        const key = targetButton.getAttribute('data-filter-key');
        const value = targetButton.getAttribute('data-filter-value');
        
        this.filterButtons.forEach(btn => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
        });
        targetButton.classList.add('is-active');
        targetButton.setAttribute('aria-pressed', 'true');
        
        if (key && value) {
          this.applyFilter({ [key]: [value] });
        }
      }
    } else if (filterKey && filterValue) {
      // New API: filter by key and value
      const targetButton = Array.from(this.filterButtons).find(
        btn => btn.getAttribute('data-filter-key') === filterKey && 
               btn.getAttribute('data-filter-value') === filterValue
      );

      if (targetButton) {
        this.filterButtons.forEach(btn => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
        });
        targetButton.classList.add('is-active');
        targetButton.setAttribute('aria-pressed', 'true');
        this.applyFilter({ [filterKey]: [filterValue] });
      }
    }
  }

  // Public API methods for programmatic control
  setFilter(filterKeyOrTag, filterValue) {
    // Support both old API (tag) and new API (key, value)
    if (filterValue) {
      // New API: setFilter(key, value)
      const event = new CustomEvent('topbar-filter:apply', {
        detail: { filterKey: filterKeyOrTag, filterValue }
      });
      document.dispatchEvent(event);
    } else {
      // Legacy API: setFilter(tag)
      const event = new CustomEvent('topbar-filter:apply', {
        detail: { tag: filterKeyOrTag }
      });
      document.dispatchEvent(event);
    }
  }

  clearFilters() {
    this.filterButtons.forEach(btn => {
      btn.classList.remove('is-active');
      btn.setAttribute('aria-pressed', 'false');
    });
    
    const allButton = Array.from(this.filterButtons).find(btn => !btn.getAttribute('data-filter-key'));
    if (allButton) {
      allButton.classList.add('is-active');
      allButton.setAttribute('aria-pressed', 'true');
    }
    
    this.applyFilter('all');
  }
}

// Define custom element
customElements.define('topbar-filter', TopbarFilter);

// Global API for triggering filters programmatically
window.TopbarFilterAPI = {
  // New API: apply filter by key and value
  apply: function(filterKeyOrTag, filterValue) {
    if (filterValue) {
      // New API: TopbarFilterAPI.apply('filter.v.option.color', 'blue')
      const event = new CustomEvent('topbar-filter:apply', {
        detail: { filterKey: filterKeyOrTag, filterValue }
      });
      document.dispatchEvent(event);
    } else {
      // Legacy API: TopbarFilterAPI.apply('bestseller')
      const event = new CustomEvent('topbar-filter:apply', {
        detail: { tag: filterKeyOrTag }
      });
      document.dispatchEvent(event);
    }
  },
  
  clear: function() {
    const topbarFilter = document.querySelector('topbar-filter');
    if (topbarFilter && topbarFilter.clearFilters) {
      topbarFilter.clearFilters();
    }
  }
};

// Example usage:
// New API:
// TopbarFilterAPI.apply('filter.p.tag', 'bestseller');
// TopbarFilterAPI.apply('filter.v.availability', 'in-stock');
// 
// Legacy API (still works):
// TopbarFilterAPI.apply('bestseller');
// 
// Clear all filters:
// TopbarFilterAPI.clear();
